
import { useEffect } from 'react';
import { useSiteSettings } from './useSiteSettings';
import { useFavicon } from './useFavicon';
import { injectStructuredData, generateOrganizationSchema, generateWebsiteSchema, generateLocalBusinessSchema } from '@/utils/seoUtils';

interface UseSEOProps {
  pageType?: 'home' | 'product' | 'category' | 'page';
  pageId?: string;
  customTitle?: string;
  customDescription?: string;
  customKeywords?: string;
  customSchema?: any;
  customImage?: string;
  fallbackTitle?: string;
  fallbackDescription?: string;
}

export const useSEO = ({ 
  pageType = 'home',
  pageId, 
  customTitle, 
  customDescription,
  customKeywords,
  customSchema,
  customImage,
  fallbackTitle,
  fallbackDescription
}: UseSEOProps) => {
  const { data: siteSettings } = useSiteSettings();

  // Initialize favicon management
  useFavicon();

  useEffect(() => {
    if (!siteSettings) return;

    // Build title with fallbacks
    const title = customTitle || 
                 fallbackTitle ||
                 siteSettings.seoTitle || 
                 `${siteSettings.siteName} - ${siteSettings.siteDescription}`;

    // Build description with fallbacks
    const description = customDescription || 
                       fallbackDescription ||
                       siteSettings.seoDescription || 
                       siteSettings.siteDescription;

    // Build keywords
    const keywords = customKeywords || siteSettings.seoKeywords || 'online store, quality products';

    // Build image URL
    const imageUrl = customImage || 
                    siteSettings.ogImageUrl || 
                    siteSettings.logoUrl ||
                    'https://lovable.dev/opengraph-image-p98pqg.png';

    // Build canonical URL
    const canonicalUrl = siteSettings.canonicalUrl || window.location.origin;
    const currentUrl = window.location.href;

    // Update document title
    document.title = title;

    // Update meta tags
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let element = document.querySelector(selector) as HTMLMetaElement;
      
      if (!element) {
        element = document.createElement('meta');
        if (property) {
          element.setAttribute('property', name);
        } else {
          element.setAttribute('name', name);
        }
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', imageUrl, true);
    updateMetaTag('og:type', pageType === 'product' ? 'product' : 'website', true);
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('og:site_name', siteSettings.businessName || siteSettings.siteName, true);

    // Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', imageUrl);
    if (siteSettings.socialLinks?.twitter) {
      updateMetaTag('twitter:site', siteSettings.socialLinks.twitter.replace('https://twitter.com/', '@'));
    }

    // Google Site Verification
    if (siteSettings.googleSiteVerification) {
      updateMetaTag('google-site-verification', siteSettings.googleSiteVerification);
    }

    // Canonical URL
    let linkElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkElement) {
      linkElement = document.createElement('link');
      linkElement.setAttribute('rel', 'canonical');
      document.head.appendChild(linkElement);
    }
    linkElement.setAttribute('href', currentUrl);

    // Google Analytics
    if (siteSettings.googleAnalyticsId) {
      // Remove existing GA script
      const existingGA = document.querySelector('script[src*="googletagmanager"]');
      if (!existingGA) {
        // Load Google Analytics
        const gaScript = document.createElement('script');
        gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${siteSettings.googleAnalyticsId}`;
        gaScript.async = true;
        document.head.appendChild(gaScript);

        // Configure Google Analytics
        const gaConfigScript = document.createElement('script');
        gaConfigScript.textContent = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${siteSettings.googleAnalyticsId}');
        `;
        document.head.appendChild(gaConfigScript);
      }
    }

    // Structured Data
    if (siteSettings.enableStructuredData) {
      // Organization Schema
      const orgSchema = generateOrganizationSchema(siteSettings);
      if (orgSchema) {
        injectStructuredData(orgSchema, 'organization-schema');
      }

      // Website Schema
      const websiteSchema = generateWebsiteSchema(siteSettings);
      if (websiteSchema) {
        injectStructuredData(websiteSchema, 'website-schema');
      }

      // Local Business Schema (if address is provided)
      const localBusinessSchema = generateLocalBusinessSchema(siteSettings);
      if (localBusinessSchema) {
        injectStructuredData(localBusinessSchema, 'local-business-schema');
      }

      // Custom schema for specific pages
      if (customSchema) {
        injectStructuredData(customSchema, `${pageType}-schema`);
      }
    }

  }, [siteSettings, customTitle, customDescription, customKeywords, customSchema, customImage, pageType, pageId, fallbackTitle, fallbackDescription]);

  return null;
};
